import { getTask } from '@/utils/stateAccessors';
import { Tooltip } from '@mui/material';
import CheckboxField from '@payaca/components/checkboxField/CheckboxField';
import { requestDeleteTask } from '@payaca/store/tasks/tasksActions';
import { FC, useCallback, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { useSelector } from '@/api/state';
import { usePermissions } from '@/hooks/usePermissions';
import CreateEditTaskSidebar from '@/ui/components/createEditTaskSidebar/CreateEditTaskSidebar';
import CustomFieldTaskViewModal from '@/ui/components/customFieldTaskViewModal/CustomFieldTaskViewModal';
import DueMessage from '@payaca/components/dueMessage/DueMessage';
import QuickActionsElement from '@payaca/components/quickActionsTableCell/QuickActionsElement';
import UserIndicator from '@payaca/components/userIndicator/UserIndicator';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import UntitledIcon from '@payaca/untitled-icons';
import ChecklistItemsProgressIndicator from '../checklistItemsProgressIndicator/ChecklistItemsProgressIndicator';
import ConfirmDeleteTaskModal from '../confirmDeleteTaskModal/ConfirmDeleteTaskModal';
import { MaterialsListTaskModal } from '../materialsListTaskModal/MaterialsListTaskModal';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import ViewCompleteTaskModal from '../viewCompleteTaskModal/ViewCompleteTaskModal';
import './TaskControl.sass';

const renderCustomDueMessage = (dueInDays: number, message: string) => {
  let className = 'past';
  if (dueInDays == 0) {
    className = 'today';
  } else if (dueInDays > 0) {
    className = 'future';
  }

  let icon = undefined;

  if (dueInDays > 0) {
    icon = <UntitledIcon name="clock.3" />;
  } else {
    icon = <UntitledIcon name="info-circle.3" />;
  }

  return (
    <div style={{ position: 'relative' }}>
      <Tooltip
        title={message}
        arrow
        placement="top"
        PopperProps={{
          className: `task-due-message-tooltip MuiTooltip-popper MuiTooltip-popperArrow ${className}`,
        }}
      >
        <div>
          <div className={`task-due-message ${className}`}>{icon}</div>
        </div>
      </Tooltip>
    </div>
  );
};

type Props = {
  dealId?: number;
  taskId: number;
  onPersistTaskSuccess?: () => void;
  onDeleteTaskSuccess?: () => void;
};
const TaskControl: FC<Props> = ({
  dealId,
  taskId,
  onPersistTaskSuccess,
  onDeleteTaskSuccess,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [showDeleteTaskWithFormModal, setShowDeleteTaskWithFormModal] =
    useState(false);
  const [showViewCompleteTaskModal, setShowViewCompleteTaskModal] =
    useState(false);
  const [showMaterialsListTaskModal, setShowMaterialsListTaskModal] =
    useState(false);
  const [showCustomFieldTaskModal, setShowCustomFieldTaskModal] =
    useState(false);
  const task = useSelector((state) => {
    return getTask(state, taskId);
  });

  const accountUsers: any[] = useSelector(
    (state: any) => state.users.accountUsers
  );

  const profile = useSelector((state: any) => state.users.myProfile);

  const assignedToUser = useMemo(() => {
    if (!task?.assignedToUserId) return;
    const user = accountUsers.find((x) => x.id === task.assignedToUserId);
    return user;
  }, [task, accountUsers]);

  const { userHasRequiredPermission } = usePermissions();

  const deleteTask = useCallback(() => {
    dispatch(requestDeleteTask(taskId, onDeleteTaskSuccess));
  }, [onDeleteTaskSuccess, dispatch, taskId]);

  const deleteTaskOrWarnDelete = useCallback(() => {
    if (task) {
      if (task?.documentId) {
        setShowDeleteTaskWithFormModal(true);
      } else {
        deleteTask();
      }
    }
  }, [deleteTask, task]);

  const isDeletingTask = useSelector((state) => {
    return state.tasks.isDeletingTask;
  });

  const quickActions = useMemo(() => {
    const quickActions: {
      isProcessing?: boolean;
      actionName: string;
      actionBehaviour: () => void;
    }[] = [];

    if (task?.type === 'materials-list') {
      return quickActions;
    }

    if (
      task?.type !== 'form' &&
      (userHasRequiredPermission([TasksPermissions.PERSIST_TASK]) ||
        (userHasRequiredPermission([
          TasksPermissions.PERSIST_SELF_CREATED_TASK,
        ]) &&
          task?.createdByUserId == profile.id) ||
        (userHasRequiredPermission([
          TasksPermissions.PERSIST_SELF_ASSIGNED_DEAL_TASK,
        ]) &&
          task?.dealId))
    ) {
      quickActions.push({
        actionName: 'Edit',
        actionBehaviour: () => setShowEditTaskModal(true),
      });
    }

    if (
      userHasRequiredPermission([TasksPermissions.DELETE_TASK]) ||
      (userHasRequiredPermission([TasksPermissions.DELETE_SELF_CREATED_TASK]) &&
        task?.createdByUserId == profile.id)
    ) {
      quickActions.push({
        isProcessing: isDeletingTask,
        actionName: 'Delete',
        actionBehaviour: deleteTaskOrWarnDelete,
      });
    }

    return quickActions;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'task.createdByUserId', 'task.type', and 'task.dealId'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: dealId
  }, [
    userHasRequiredPermission,
    isDeletingTask,
    deleteTaskOrWarnDelete,
    dealId,
    profile,
  ]);

  const showDueMessage = useMemo(() => {
    if (!task) return false;
    if (task.completedAt) return false;
    if (!task.deadlineDate) return false;

    return true;
  }, [task]);

  const onClickTask = useCallback(() => {
    if (!task) return;
    switch (task.type) {
      case 'custom-fields':
        setShowCustomFieldTaskModal(true);
        break;
      case 'materials-list':
        setShowMaterialsListTaskModal(true);
        break;
      case 'checklist':
      case 'legacy-form':
        setShowViewCompleteTaskModal(true);
        break;
      case 'form':
        // no-op
        break;
    }
  }, [task]);

  if (!task) return null;

  return (
    <>
      <div className="task-control flex space-x-3.5 items-center">
        <div
          className="task-control-panel flex space-x-3.5 items-center clickable grow"
          onClick={onClickTask}
        >
          <div>
            <CheckboxField name="isCompleted" value={!!task.completedAt} />
          </div>
          <div className="flex space-x-3.5 items-center task-body-container grow">
            <div className="grow">
              <div>
                <span>{task.name}</span>
              </div>
              <div className="checklist-container">
                <ChecklistItemsProgressIndicator
                  checklistItems={task.checklistItems}
                />
              </div>
            </div>
            <div className="task-details-container flex space-x-3.5 items-center">
              {showDueMessage && task.deadlineDate && (
                <DueMessage
                  dueDate={task.deadlineDate}
                  renderMessage={renderCustomDueMessage}
                />
              )}
              {assignedToUser && (
                <PermissionGuard
                  renderIfHasPermissions={[AccountsPermissions.GET_USERS]}
                >
                  <div>
                    <UserIndicator
                      user={{
                        firstName: assignedToUser.firstname,
                        lastName: assignedToUser.lastname,
                        emailAddress: assignedToUser.email,
                        userColour: assignedToUser.userColour,
                        imgSrc: assignedToUser.avatarUrl,
                      }}
                      isUserInformationHoverPopperEnabled={true}
                    />
                  </div>
                </PermissionGuard>
              )}
            </div>
          </div>
        </div>
        <div>
          {task.createdByUserId != profile.id && !quickActions?.length ? (
            <div style={{ position: 'relative' }}>
              <Tooltip
                title={`No actions available against tasks you didn't create yourself`}
                arrow
                placement="top"
                PopperProps={{
                  className: `task-disabled-quick-actions-tooltip  MuiTooltip-popper MuiTooltip-popperArrow`,
                }}
              >
                <div>
                  <QuickActionsElement
                    recordId={task.id}
                    quickActions={quickActions}
                    renderDisabledIfNoActions={true}
                  />
                </div>
              </Tooltip>
            </div>
          ) : (
            <QuickActionsElement
              recordId={task.id}
              quickActions={quickActions}
              renderDisabledIfNoActions={true}
            />
          )}
        </div>
      </div>
      <MaterialsListTaskModal
        isOpen={showMaterialsListTaskModal}
        onClose={() => {
          setShowMaterialsListTaskModal(false);
        }}
        taskId={task.id}
      />
      <CustomFieldTaskViewModal
        isOpen={showCustomFieldTaskModal}
        onClose={() => {
          setShowCustomFieldTaskModal(false);
        }}
        taskId={task.id}
      />
      <CreateEditTaskSidebar
        isOpen={showEditTaskModal}
        onClose={() => setShowEditTaskModal(false)}
        taskId={task.id}
        projectId={dealId}
        onSuccess={onPersistTaskSuccess}
      />
      <ViewCompleteTaskModal
        task={task}
        isOpen={showViewCompleteTaskModal}
        onClose={() => setShowViewCompleteTaskModal(false)}
        onUpdateTaskCompletionSuccess={() => {
          setShowViewCompleteTaskModal(false);
          if (onPersistTaskSuccess) {
            // oxlint-disable-next-line typescript/no-meaningless-void-operator -- FIXME: pre-existing error: void operator shouldn't be used on void; it should convey that a return value is being ignored
            void onPersistTaskSuccess();
          }
        }}
      />
      <ConfirmDeleteTaskModal
        isOpen={showDeleteTaskWithFormModal}
        onClose={() => setShowDeleteTaskWithFormModal(false)}
        taskHasDocument={!!task.documentId}
        onConfirmDeleteTask={deleteTask}
      />
    </>
  );
};

export default TaskControl;
